'use client';

import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import useMediaQuery from 'hooks/useMediaQuery';
import Link from 'next/link';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

interface TimelineItem {
    id: number;
    title: string;
    content: string;
    category: string;
    icon: any;
    link: string;
}

interface RadialOrbitalTimelineProps {
    timelineData: TimelineItem[];
    moduleIcon: any;
}

// Configuration
const MOBILE_RADIUS = 120;
const TABLET_RADIUS = 160;
const DESKTOP_RADIUS = 200;
const STOP_ANGLE = 270;

const OrbitNode = memo(
    ({
        item,
        index,
        total,
        rotationValue,
        radius,
        isExpanded,
        onToggle
    }: {
        item: TimelineItem;
        index: number;
        total: number;
        rotationValue: any;
        radius: number;
        isExpanded: boolean;
        onToggle: (id: number) => void;
    }) => {
        const baseAngle = (index / total) * 360;

        const x = useTransform(rotationValue, (rotation) => {
            // @ts-ignore
            const angle = (baseAngle + rotation) % 360;
            const radian = (angle * Math.PI) / 180;
            return radius * Math.cos(radian);
        });

        const y = useTransform(rotationValue, (rotation) => {
            // @ts-ignore
            const angle = (baseAngle + rotation) % 360;
            const radian = (angle * Math.PI) / 180;
            return radius * Math.sin(radian);
        });

        const zIndex = useTransform(rotationValue, (rotation) => {
            if (isExpanded) return 1000; // Always top when expanded
            // @ts-ignore
            const angle = (baseAngle + rotation) % 360;
            const radian = (angle * Math.PI) / 180;
            return Math.round(100 + 50 * Math.sin(radian));
        });

        const handleClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                onToggle(item.id);
            },
            [item.id, onToggle]
        );

        return (
            <motion.div
                className="tw-absolute tw-flex tw-items-center tw-justify-center"
                style={{
                    x,
                    y,
                    zIndex,
                    width: '3rem', // Fixed size container to ensure center alignment
                    height: '3rem'
                }}
                onClick={handleClick}
            >
                <div
                    className={`tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-transition-all tw-duration-500 tw-ease-out ${
                        isExpanded
                            ? 'tw-min-h-full tw-min-w-full tw-border-success tw-bg-success tw-text-white'
                            : 'tw-h-10 tw-w-10 tw-border-successLight/30 tw-bg-accents-4 tw-text-accents-6 hover:tw-border-success hover:tw-bg-successLight'
                    } `}
                >
                    <FontAwesomeIcon
                        icon={item.icon}
                        className="tw-text-xs tw-transition-transform group-hover:tw-translate-x-1"
                    />
                </div>

                <div
                    className={`tw-absolute tw-top-14 tw-whitespace-nowrap tw-text-[11px] tw-font-bold tw-tracking-wider tw-transition-all tw-duration-300 ${isExpanded ? 'tw-scale-0 tw-opacity-0' : 'tw-text-accents-6 tw-opacity-100'} `}
                >
                    {item.title}
                </div>

                {/* Popover */}
                {isExpanded && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="tw-card-bordered tw-absolute tw-top-[110%] tw-w-72 tw-cursor-default tw-rounded-xl tw-bg-white/90 tw-p-5 tw-shadow-xl tw-shadow-successLight/30 tw-backdrop-blur-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="tw-mb-1 tw-text-lg tw-font-bold tw-text-gray-900">{item.title}</div>
                        <div className="tw-mb-3 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-success">
                            {item.category}
                        </div>
                        <p className="tw-mb-4 tw-text-sm tw-leading-relaxed tw-text-gray-600">{item.content}</p>

                        <div className="tw-flex tw-justify-end tw-border-t tw-border-gray-100 tw-pt-3">
                            <Link
                                href={item.link}
                                className="tw-group tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-success tw-transition-colors hover:tw-text-success/80"
                            >
                                Mehr Erfahren
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                    className="tw-text-xs tw-transition-transform group-hover:tw-translate-x-1"
                                />
                            </Link>
                        </div>
                    </motion.div>
                )}
            </motion.div>
        );
    }
);

OrbitNode.displayName = 'OrbitNode';

export function OrbitePopver({ timelineData, moduleIcon }: RadialOrbitalTimelineProps) {
    const isTablet = useMediaQuery('md');
    const isMobile = useMediaQuery('sm');
    const radius = isMobile ? MOBILE_RADIUS : isTablet ? TABLET_RADIUS : DESKTOP_RADIUS;
    const containerHeight = isMobile ? 420 : isTablet ? 520 : 600;
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [autoRotate, setAutoRotate] = useState(true);

    const containerRef = useRef<HTMLDivElement>(null);
    const rotationValue = useMotionValue(0);
    const animationRef = useRef<any>(null);

    useEffect(() => {
        if (autoRotate) {
            animationRef.current = animate(rotationValue, rotationValue.get() + 360, {
                duration: 45,
                repeat: Infinity,
                ease: 'linear'
            });
        } else {
            animationRef.current?.stop();
        }
        return () => animationRef.current?.stop();
    }, [autoRotate, rotationValue]);

    const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        setExpandedId(null);
        setAutoRotate(true);
    }, []);

    const toggleItem = useCallback(
        (id: number) => {
            setExpandedId((prev) => {
                const newId = prev === id ? null : id;
                const isOpening = newId !== null;

                setAutoRotate(!isOpening);

                if (isOpening) {
                    const nodeIndex = timelineData.findIndex((item) => item.id === id);
                    if (nodeIndex === -1) return newId;

                    const nodeBaseAngle = (nodeIndex / timelineData.length) * 360;

                    const currentRotation = rotationValue.get();

                    const currentAngle = (nodeBaseAngle + currentRotation) % 360;

                    let diff = STOP_ANGLE - currentAngle;

                    while (diff < -180) diff += 360;
                    while (diff > 180) diff -= 360;

                    const targetRotation = currentRotation + diff;

                    animationRef.current?.stop();
                    animate(rotationValue, targetRotation, {
                        type: 'spring',
                        stiffness: 50,
                        damping: 15,
                        mass: 1
                    });
                }

                return newId;
            });
        },
        [timelineData, rotationValue]
    );

    return (
        <div
            className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden tw-bg-transparent"
            style={{ height: containerHeight }}
            ref={containerRef}
            onClick={handleContainerClick}
        >
            {/* Background / Decorative Elements */}
            <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                {/* Outer faint ring */}
                <div
                    className="tw-absolute tw-rounded-full tw-border tw-border-dashed tw-border-gray-200"
                    style={{ width: radius * 2 + 150, height: radius * 2 + 150 }}
                />
                {/* The Main Track */}
                <div
                    className="tw-absolute tw-rounded-full tw-border tw-border-gray-300/40"
                    style={{ width: radius * 2, height: radius * 2 }}
                />
            </div>

            <div className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
                {/* Center Core */}
                <div className="tw-relative tw-z-0 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-shadow-2xl tw-shadow-success/50 md:tw-h-24 md:tw-w-24">
                    <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-success tw-text-white md:tw-h-16 md:tw-w-16">
                        <FontAwesomeIcon icon={moduleIcon} size="2x" />
                    </div>
                </div>

                {/* Rotating Container Layer */}
                <div
                    className="tw-absolute tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"
                    style={{ perspective: '1000px' }}
                >
                    {timelineData.map((item, index) => (
                        <OrbitNode
                            key={item.id}
                            item={item}
                            index={index}
                            total={timelineData.length}
                            rotationValue={rotationValue}
                            radius={radius}
                            isExpanded={expandedId === item.id}
                            onToggle={toggleItem}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
